import ListViewModel from "viewModels/listViewModel";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation/dist";
import ExternalReportsRepository from "repositories/externalReportsRepository";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import ExternalReportType from "entities/externalReportType";
import ExternalReportResponse from "entities/externalReportResponse";
import QuarterOfYearRangeRequest, { QuarterOfYearValidationRules } from "entities/quarterOfYearRangeRequest";
import { watchBusy } from "utils/busyWatcher";
import { allowToDownload } from "../rights";
import { Modules } from "config";
import * as Sentry from "@sentry/react";
import QuarterOptionsService from "services/quarterOptionsService";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import type ExternalReportDetailViewModel from "../externalReportDetailViewModel";

const navigationName = "czech-statistical-office";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class CzechStatisticalOfficeReportsListViewModel extends ListViewModel<
  ExternalReportResponse,
  ExternalReportDetailViewModel,
  ScreenBase,
  any
> {
  readonly modelName = "externalReportResponse";
  @observable items: ExternalReportResponse[] = [];
  @observable fileResponse: any = undefined;
  @observable request = new QuarterOfYearRangeRequest();

  readonly idProperty = "id";

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get quarterOptions() {
    return this.quarterOptionService.quarterOptions.reverse();
  }

  @computed
  get quarterOptionsTo() {
    if (!this.request.from) {
      return this.quarterOptions;
    }

    // We want to take only the values until the previously selected one.
    const result: any[] = [];
    this.quarterOptions.some(x => (result.push(x), x.id === this.request.from));

    return result;
  }

  @computed
  get canMakeReport() {
    return this.securityService.allowTo("charges.reports", "write");
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof ExternalReportDetailViewModel.Factory>,
    public repository: ExternalReportsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService,
    private quarterOptionService: QuarterOptionsService
  ) {
    super();
    this.navigationName = navigationName;

    attachAutomaticValidator(this.request, QuarterOfYearValidationRules);
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `externalReports.czechStatisticalOffice.${code}`, size);
  }

  @action.bound
  @watchBusy
  async onFormSubmit() {
    if (!validate(this.request)) {
      return;
    }
    await this.onGenerateReportClick(this.request.from, this.request.to);
  }

  @action.bound
  @watchBusy
  async onGenerateReportClick(fromQuarter: string, toQuarter: string) {
    try {
      const result = await this.repository.generateNewCzechStatisticalOfficeReport(fromQuarter, toQuarter);

      if (result.reportRequestId) {
        const reportName = `csu_report_${new Date().toISOString()}.xlsx`;

        await this.download({ id: result.reportRequestId, reportName: reportName, createdBy: "", status: result.reportStatus });
        this.notificationService.add({ title: this.translate("alreadyGeneratedMessage"), type: NotificationType.SUCCESS });
      } else {
        this.notificationService.add({ title: this.translate("generatingMessage"), type: NotificationType.SUCCESS });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @action.bound
  @watchBusy
  async onDownload(item: ExternalReportResponse) {
    await this.download(item);
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  protected async fetchData() {
    const reports = await this.repository.getExternalReports(ExternalReportType.Cso);

    reports.items = reports.items.sort((a, b) => {
      if (!a.processedAt || !b.processedAt) {
        return -1;
      }
      return b.processedAt.getTime() - a.processedAt.getTime();
    });

    return reports;
  }

  async download(item: ExternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.reportName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
